import { useState, useContext, useEffect, useCallback, useRef } from 'react' 
import { ReactSortable } from 'react-sortablejs'

import ActionsDropdown from '../../UI/ActionsDropdown'
import CustomTooltip from '../../UI/CustomTooltip'
import { ResizableVariableColumn, VariablesRow, VariablesActions, ColumnsFilter, CreateEditVariableModal, ImportVariablesModal } from '../'
import { GlobalContext, VariablesContext, TeamContext, ColumnsContext } from '../../../context'
import { getTeamPaginationData, getTeamColumnsOrderData, getTeamColumnsData, isCookieAccepted, saveTeamPaginationDataToLS, saveTeamColumnsOrderDataToLS, removeTeamColumnsOrderData, saveTeamVarsColumnWidthsToLS } from '../../../utils'
import { perPageOptions } from '../../../constants'

const VariablesView = ({ filteredVariables, onSort, selectedVariables, setSelectedVariables, allChecked, onAllCheckedChange, paginatedData, setPaginatedData, refreshVariables, ready, setAllChecked }) => {
  const { t } = useContext(GlobalContext)
  const { setVariablesSelectedFilters } = useContext(VariablesContext)
  const { selectedTeam } = useContext(TeamContext)
  const { varsTableWidth, setVarsTableWidth, varsTableColumnsWidths, setVarsTableColumnsWidths } = useContext(ColumnsContext)
  const [defaultFields, setDefaultFields] = useState([])
  const [columns, setColumns] = useState([
    { label: t('general.name'), value: 'variable', icon: 'text_fields', checked: true },
    { label: t('general.tooltip'), value: 'tooltip', icon: 'chat_bubble', checked: true },
    { label: t('general.category'), value: 'category_sort', icon: 'category', checked: true },
    { label: t('general.question'), value: 'question', icon: 'question_mark', checked: true },
    { label: t('dashboard.created_at'), value: 'meta.created', icon: 'calendar_month', checked: true },
    { label: t('dashboard.modified'), value: 'meta.updated', icon: 'calendar_month', checked: true },
  ])
  const [search, setSearch] = useState('')
  const [perPage, setPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [allPages, setAllPages] = useState(1)
  const [paginationReady, setPaginationReady] = useState(false)
  const [columnsUpdated, setColumnsUpdated] = useState(false)
  const [activeResizableColumn, setActiveResizableColumn] = useState(null)
  const [tableWidthReady, setTableWidthReady] = useState(false)
  const [showCreateVariableModal, setShowCreateVariableModal] = useState(false)
  const [showImportVariablesModal, setShowImportVariablesModal] = useState(false)
  const colRefs = useRef([])

  // Check in LS to see if pagination data was saved and if it is saved update pagination state variables
  useEffect(() => {
    const paginationData = getTeamPaginationData()
    if(selectedTeam && paginationData && paginationData[selectedTeam.id] && paginationData[selectedTeam.id].variables) {
      const data = paginationData[selectedTeam.id].variables
      let all = Math.ceil(filteredVariables.length / data.items || 10)
      data.items && setPerPage(data.items)
      data.current && setCurrentPage(data.current <= all ? data.current : 1)
      data.all && setAllPages(data.all)
    }
  }, [filteredVariables, selectedTeam])

  // order columns...
  useEffect(() => {
    if(selectedTeam && !columnsUpdated) {
      let shouldUpdate = false
      let newColumns = [...columns]
      setDefaultFields([...columns, { label: '', value: 'helper', icon: '', checked: true }])

      // Show only checked columns
      const teamColData = getTeamColumnsData()
      if(teamColData && teamColData[selectedTeam.id]) {
        const colsValues = teamColData[selectedTeam.id].variables
        let arr = []
        if(colsValues) {
          arr = newColumns.map(c => colsValues.includes(c.value) ? {...c, checked: true} : {...c, checked: c.value !== 'variable' ? false : true})
          newColumns = arr
        }
      }

      // Change order
      const colOrderValues = getTeamColumnsOrderData()
      if(colOrderValues && colOrderValues[selectedTeam.id]) {
        const collectionColOrderValues = colOrderValues[selectedTeam.id].variables 
        if(collectionColOrderValues) {
          let orderedCols = [] 
          collectionColOrderValues.forEach(val => {
            const col = newColumns.find(c => c.value === val)
            col && orderedCols.push(col)
          })
          // Check if there are some columns that were not saved to columns order array, if so add them 
          const filterHiddenValues = [] 
          newColumns.forEach(c => {
            if(!collectionColOrderValues.includes(c.value)) {
              filterHiddenValues.push(c)
            }
          })
          if(filterHiddenValues.length > 0) {
            orderedCols.push(...filterHiddenValues)
          }
          newColumns = orderedCols
          shouldUpdate = true
        }
      }

      // Add helper column
      newColumns.push({ label: '', value: 'helper', icon: '', checked: true })
      shouldUpdate = true

      // Update only if new columns are added or order changed
      if(shouldUpdate) {
        setColumns(newColumns)
      }
      setColumnsUpdated(true)
    }
  }, [selectedTeam, columnsUpdated, columns, t])

  // Calculate table width
  const calculateTableWidth = useCallback(() => {
    let width = 0
    let columnsSaved = false
    let savedColumns = []
    const carbonColumns = getTeamColumnsData()
    if(carbonColumns && carbonColumns[selectedTeam?.id]) {
      const cols = carbonColumns[selectedTeam?.id].variables
      if(cols) {
        columnsSaved = true
        savedColumns = cols
      }
    }
    
    columns.forEach(col => {
      if(col.value !== 'helper') {
        if(columnsSaved) {
          if(savedColumns.includes(col.value)) {
            width += varsTableColumnsWidths[col.value] || 200
          }
        }else {
          if(col.checked) {
            width += varsTableColumnsWidths[col.value] || 200
          }
        }
      }
    })
    setVarsTableWidth(width)
  }, [columns, selectedTeam, setVarsTableWidth, varsTableColumnsWidths])

  // Set width to each column and calculate table width
  useEffect(() => {
    if(!tableWidthReady) {
      calculateTableWidth()
      setTableWidthReady(true)
    }
  }, [calculateTableWidth, tableWidthReady])

  // Change paginated data arr when filtered arr changes
  useEffect(() => {
    let current = currentPage
    let sliceFrom = current === 1 ? 0 : (current - 1) * perPage
    let slicedData
    if(sliceFrom < filteredVariables.length) {
      slicedData = filteredVariables.slice(sliceFrom, sliceFrom + perPage)
    }else {
      slicedData = filteredVariables.slice(0, perPage)
    }
    setPaginatedData(slicedData)
    let all = Math.ceil(filteredVariables.length / perPage)
    setAllPages(all === 0 ? 1 : all)
    setTimeout(() => setPaginationReady(true), 200)
  }, [filteredVariables, perPage, currentPage, allPages, setPaginatedData])

  // On search change
  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setVariablesSelectedFilters(prev => ({...prev, search: e.target.value}))
    setSelectedVariables([])
  }

  // On prev button click 
  const handlePrevButtonClick = (e) => {
    e.preventDefault()
    if(currentPage === 1) return 
    setCurrentPage(currentPage - 1)
    setSelectedVariables([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, currentPage - 1, allPages, 'variables')
    }
  }

  // On next button click 
  const handleNextButtonClick = (e) => {
    e.preventDefault()
    if(currentPage === allPages) return 
    setCurrentPage(currentPage + 1)
    setSelectedVariables([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, currentPage + 1, allPages, 'variables')
    }
  }

  // On go to specific page
  const handleGoToPage = (e, page) => {
    e.preventDefault()
    if(currentPage === page) return 
    setCurrentPage(page)
    setSelectedVariables([])
    setAllChecked(false)
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, perPage, page, allPages, 'variables')
    }
  }

  // Per page change
  const handlePerPageChange = (e, num) => {
    e.preventDefault()
    setPerPage(num)
    setSelectedVariables([])
    setAllChecked(false)
    if(Math.ceil(filteredVariables.length / num) < currentPage) {
      setCurrentPage(1)
    }
    window.document.body.click()
    if(isCookieAccepted()) {
      saveTeamPaginationDataToLS(selectedTeam?.id, num, currentPage, allPages, 'variables')
    }
  }

  // Save columns order
  const handleSaveColumnsOrder = useCallback(() => {
    const colOrderValues = getTeamColumnsOrderData()
    if(isCookieAccepted()) {
      let arr = columns.map(f => f.value)
      saveTeamColumnsOrderDataToLS(selectedTeam?.id, arr, 'variables')
    }else {
      if(colOrderValues) {
        removeTeamColumnsOrderData()
      } 
    }
  }, [columns, selectedTeam])

  // On mouse down - set active resizable column
  const handleMouseDown = useCallback((idx, val) => {
    setActiveResizableColumn({ index: idx, value: val })
  }, [])

  // On mouse move - only when there is active resizable column
  const handleMouseMove = useCallback((e) => {
    if(selectedTeam && activeResizableColumn !== null) {
      const el = colRefs.current[activeResizableColumn.index]
      if(!el) return
      const rect = el.getBoundingClientRect()
      const width = e.clientX - rect.x

      if(width > 200) {
        el.style.width = width + 'px'
        let newWidths
        newWidths = {...varsTableColumnsWidths}
        newWidths[activeResizableColumn.value] = width
        setVarsTableColumnsWidths(newWidths)

        let totalWidth = 0
        columns.forEach(col => {
          if(col.value !== 'helper') {
            totalWidth += newWidths[col.value] || 200
          }
        })
        setVarsTableWidth(totalWidth)
        if(isCookieAccepted()) {
          saveTeamVarsColumnWidthsToLS(selectedTeam.id, newWidths)
        }
      }
    }
    // eslint-disable-next-line
  }, [activeResizableColumn, columns, selectedTeam])

  // Add mouse event listeners
  useEffect(() => {
    const handleMouseUp = () => {
      setActiveResizableColumn(null)
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }

    if (activeResizableColumn !== null) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    }
  }, [activeResizableColumn, handleMouseMove])

  // On open create variable modal
  const handleOpenCreateVariableModal = () => {
    setShowCreateVariableModal(true)
  }

  // On close create variable modal
  const handleCloseCreateVariableModal = () => {
    setShowCreateVariableModal(false)
  }

  // On open create variable modal
  const handleOpenImportVariablesModal = () => {
    setShowImportVariablesModal(true)
  }

  // On close create variable modal
  const handleCloseImportVariablesModal = () => {
    setShowImportVariablesModal(false)
  }

  return (
    <div className="list-view-v2">
      <div className="list-view-v2__head">
        <div className="search">
          <div className="icon">
            <span className="material-symbols-outlined weight-300">search</span>
          </div>
          <input type="text" value={search} onChange={handleSearchChange} />
        </div>
        <div className="per-page">
          <ActionsDropdown
            trigger={<a href="#/" className="text-style-4">{perPage} {t('general.per_page')} <span className="material-symbols-outlined">expand_more</span></a>}
            hideHeader
            dropdownClass="pagination-dropdown"
            width={120}
          >
            <ul>
              {perPageOptions.map((num, i) => (
                <li key={i}>
                  <div className="link">
                    <a href="/#" onClick={(e) => handlePerPageChange(e, num)}>{num}</a>
                  </div>
                </li>
              ))}
            </ul>
          </ActionsDropdown>
        </div>
        <div className="actions">
          <CustomTooltip content={t('dashboard.create_variable')} spaceSm>
            <button className="icon-btn icon-btn--small" onClick={handleOpenCreateVariableModal}>
              <span className="material-symbols-outlined">add</span>
            </button>
          </CustomTooltip>
          <CustomTooltip content={t('dashboard.import_variables')} spaceSm>
            <button className="icon-btn icon-btn--small" onClick={handleOpenImportVariablesModal}>
              <span className="material-symbols-outlined">upload</span>
            </button>
          </CustomTooltip>
        </div>
        <div className="filter">
          <ColumnsFilter 
            columns={columns} 
            setColumns={setColumns} 
            onOrderSave={handleSaveColumnsOrder} 
            view="variables" 
            defaultFields={defaultFields}
            calculateTableWidth={calculateTableWidth}
          />
        </div>
      </div>

      <div className="list-view-v2__body">
        <div className="list-view-table-wrapper u-custom-scrollbar u-custom-scrollbar--3">
          {selectedVariables.length > 0 && (
            <VariablesActions allChecked={allChecked} onAllCheckedChange={onAllCheckedChange} setSelectedItems={setSelectedVariables} selectedItems={selectedVariables} refreshVariables={refreshVariables} />
          )}
          <div className="list-view-table" style={{minWidth: varsTableWidth}}>
            <div className="list-view-table__head">
              <ReactSortable 
                list={columns} 
                setList={setColumns} 
                animation={200} 
                delayOnTouchStart={true} 
                delay={2} 
                dragClass="dragged-field--2"
                ghostClass="selected-field"
                filter=".filtered-out"
                draggable=".draggable-item"
                onEnd={handleSaveColumnsOrder}
                handle=".sort"      
                className="sortable-row-items"  
              >
                {columns.filter(c => c.checked).map((column, idx) => {
                  return (
                    <ResizableVariableColumn column={column} idx={idx} onMouseDown={handleMouseDown} onSort={onSort} ref={(item) => colRefs.current[idx] = item} key={idx} />
                  )
                })}
              </ReactSortable>
              <div className="list-view-table__cell checkbox">
                {!allChecked 
                  ? <span className="material-symbols-outlined blank one" onClick={onAllCheckedChange}>check_box_outline_blank</span>
                  : <span className="material-symbols-outlined filled" onClick={onAllCheckedChange}>check_box</span>
                }
              </div>
            </div>
            <div className="list-view-table__body">
              {(!ready || !paginationReady) && (
                <div className="loader-wrapper"><div className="loader-v2"></div></div>
              )}
              {ready && paginationReady && paginatedData.map((doc, idx) => {

                return (
                  <VariablesRow 
                    key={idx} 
                    doc={doc}
                    id={doc.id}
                    selectedVariables={selectedVariables}
                    setSelectedVariables={setSelectedVariables}
                    allChecked={allChecked}
                    columns={columns}
                    refreshVariables={refreshVariables}
                  />
                )
              })}
              {ready && paginationReady && paginatedData.length === 0 && (
                <div className="no-data">
                  <p className="text-style-3">{t('folder.empty')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="list-view-v2__foot">
        <div className="pagination-v2">
          <a href="/#" className="prev" onClick={handlePrevButtonClick}><span className="material-symbols-outlined weight-300">arrow_back</span></a>
          {Array(allPages).fill().map((_, idx) => {
            return (
              <a href="/#" key={idx} className={`${idx+1 === currentPage ? 'active' : ''}`} onClick={(e) => handleGoToPage(e, idx+1)}>
                {idx+1}
              </a>
            )
          })}
          <a href="/#" className="next" onClick={handleNextButtonClick}><span className="material-symbols-outlined weight-300">arrow_forward</span></a>
        </div>
      </div>

      {showCreateVariableModal && (
        <CreateEditVariableModal 
          onClose={handleCloseCreateVariableModal}
          refreshVariables={refreshVariables}
          lastSelectedCategory=""
          onCategorySelected={() => {}}
        />
      )}

      {showImportVariablesModal && (
        <ImportVariablesModal onClose={handleCloseImportVariablesModal} refreshVariables={refreshVariables} />
      )}
    </div>
  )
}

export default VariablesView